import logger from '../../utils/logger';

/**
 * Geolocation utilities for competitor analysis
 */

const GEO_PROXY_BASE_URL = process.env.REACT_APP_GEO_PROXY_URL || 'http://localhost:4000/api/geocode';

/**
 * Get grid label for display (e.g., A1, B2, etc.)
 */
export const getGridLabel = (gridX, gridY) => {
  const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  return `${letters[gridX] || gridX}${gridY + 1}`;
};

/**
 * Calculate distance between two coordinates in kilometers
 */
export const calculateDistance = (coord1, coord2) => {
  const R = 6371; // Earth's radius in kilometers
  const dLat = (coord2.lat - coord1.lat) * Math.PI / 180;
  const dLon = (coord2.lng - coord1.lng) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(coord1.lat * Math.PI / 180) * Math.cos(coord2.lat * Math.PI / 180) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
};

/**
 * Generate grid points around a center location
 */
export const generateGridPoints = (centerLat, centerLng, gridSize, radiusKm) => {
  // Validate inputs
  if (typeof centerLat !== 'number' || isNaN(centerLat)) {
    logger.error('❌ Invalid centerLat:', centerLat);
    throw new Error(`Invalid centerLat: ${centerLat}`);
  }
  if (typeof centerLng !== 'number' || isNaN(centerLng)) {
    logger.error('❌ Invalid centerLng:', centerLng);
    throw new Error(`Invalid centerLng: ${centerLng}`);
  }
  if (typeof gridSize !== 'number' || isNaN(gridSize) || gridSize <= 0) {
    logger.error('❌ Invalid gridSize:', gridSize);
    throw new Error(`Invalid gridSize: ${gridSize}`);
  }
  if (typeof radiusKm !== 'number' || isNaN(radiusKm) || radiusKm <= 0) {
    logger.error('❌ Invalid radiusKm:', radiusKm);
    throw new Error(`Invalid radiusKm: ${radiusKm}`);
  }

  logger.log('✅ Generating grid with valid inputs:', { centerLat, centerLng, gridSize, radiusKm });

  const points = [];
  const gridSpacing = (radiusKm * 2) / gridSize; // Distance between grid points
  const kmPerDegree = 111; // Approximate km per degree of latitude
  
  for (let x = 0; x < gridSize; x++) {
    for (let y = 0; y < gridSize; y++) {
      const offsetX = (x - (gridSize - 1) / 2) * (gridSpacing / kmPerDegree);
      const offsetY = (y - (gridSize - 1) / 2) * (gridSpacing / (kmPerDegree * Math.cos(centerLat * Math.PI / 180)));
      
      const lat = centerLat + offsetX;
      const lng = centerLng + offsetY;
      
      points.push({
        gridX: x,
        gridY: y,
        lat: lat,
        lng: lng,
        label: getGridLabel(x, y)
      });
    }
  }
  
  logger.log(`✅ Generated ${points.length} grid points`);
  return points;
};

/**
 * Get current user location using browser geolocation API
 */
export const getCurrentLocation = () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported by this browser'));
      return;
    }
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          lat: position.coords.latitude,
          lng: position.coords.longitude
        });
      },
      (error) => {
        reject(error);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000 // 5 minutes
      }
    );
  });
};

/**
 * Geocode address to coordinates using backend proxy
 */
export const geocodeAddress = async (address) => {
  try {
    const url = new URL(`${GEO_PROXY_BASE_URL}/search`);
    url.searchParams.set('address', address);

    const response = await fetch(url.toString());
    if (!response.ok) {
      throw new Error('Geocoding service unavailable');
    }

    const data = await response.json();
    const location = data?.location;
    if (location && typeof location.lat === 'number' && typeof location.lng === 'number') {
      return { lat: location.lat, lng: location.lng };
    }

    throw new Error('No coordinates found for address');
  } catch (error) {
    throw new Error(`Failed to geocode address: ${error.message}`);
  }
};

/**
 * Reverse geocode coordinates to address using Google Geocoding API
 */
export const reverseGeocode = async (lat, lng, apiKey) => {
  try {
    const url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${apiKey}&result_type=street_address|premise|subpremise|locality|sublocality|administrative_area_level_2|administrative_area_level_1|postal_code`;

    const response = await fetch(url);
    if (!response.ok) {
      throw new Error('Reverse geocoding service unavailable');
    }

    const data = await response.json();
    
    if (data.status === 'OK' && data.results && data.results.length > 0) {
      return extractCleanAddressFromGoogleResults(data.results);
    }

    return `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
  } catch (error) {
    logger.warn('Reverse geocoding failed:', error);
    return `${lat.toFixed(4)}, ${lng.toFixed(4)} (Location Unknown)`;
  }
};

/**
 * Extract clean address from Google geocoding results
 */
function extractCleanAddressFromGoogleResults(results) {
  try {
    let locality = '';
    let subLocality = '';
    let administrativeArea = '';
    let postalCode = '';
    let country = '';

    for (const result of results) {
      if (result.address_components) {
        for (const component of result.address_components) {
          const types = component.types;

          if (types.includes('locality') && !locality) {
            locality = component.long_name;
          } else if (types.includes('administrative_area_level_2') && !locality) {
            locality = component.long_name;
          } else if ((types.includes('sublocality') || types.includes('sublocality_level_1') || types.includes('neighborhood')) && !subLocality) {
            subLocality = component.long_name;
          } else if (types.includes('administrative_area_level_1') && !administrativeArea) {
            administrativeArea = component.long_name;
          } else if (types.includes('postal_code') && !postalCode) {
            postalCode = component.long_name;
          } else if (types.includes('country') && !country) {
            country = component.long_name;
          }
        }
      }
    }

    let cleanAddress = '';

    if (locality) {
      cleanAddress += locality;
    }
    if (subLocality) {
      cleanAddress += cleanAddress ? `, ${subLocality}` : subLocality;
    }

    if (postalCode) {
      cleanAddress += cleanAddress ? ` - ${postalCode}` : postalCode;
    }

    if (administrativeArea) {
      cleanAddress += cleanAddress ? `, ${administrativeArea}` : administrativeArea;
    }

    if (country) {
      cleanAddress += cleanAddress ? `, ${country}` : country;
    }

    if (!cleanAddress && results.length > 0) {
      let fallbackAddress = results[0].formatted_address || '';
      fallbackAddress = fallbackAddress.replace(/[A-Z0-9]{4}\+[A-Z0-9]{2},?\s*/g, '');
      fallbackAddress = fallbackAddress.replace(/Unnamed Road,?\s*/gi, '');
      cleanAddress = fallbackAddress;
    }

    return cleanAddress || 'Unknown Location';
  } catch (error) {
    logger.warn('Error extracting clean address from Google response:', error);
    return results[0]?.formatted_address || 'Unknown Location';
  }
}

/**
 * Check if coordinates are within a certain radius of a center point
 */
export const isWithinRadius = (center, point, radiusKm) => {
  const distance = calculateDistance(center, point);
  return distance <= radiusKm;
};

/**
 * Get bounding box for a center point and radius
 */
export const getBoundingBox = (centerLat, centerLng, radiusKm) => {
  const kmPerDegree = 111;
  const latOffset = radiusKm / kmPerDegree;
  const lngOffset = radiusKm / (kmPerDegree * Math.cos(centerLat * Math.PI / 180));
  
  return {
    north: centerLat + latOffset,
    south: centerLat - latOffset,
    east: centerLng + lngOffset,
    west: centerLng - lngOffset
  };
};

/**
 * Generate grid points with addresses (for TS compatibility)
 */
export const generateGridPointsWithAddresses = async (
  centerLocation,
  radiusKm,
  gridSize,
  apiKey,
  useMock = false,
  onProgress = null
) => {
  logger.log('🗺️ Generating grid points:', { centerLocation, radiusKm, gridSize });
  const gridPoints = generateGridPoints(centerLocation.lat, centerLocation.lng, gridSize, radiusKm);
  logger.log('📍 Generated grid points:', gridPoints.map(p => ({ label: p.label, lat: p.lat, lng: p.lng })));
  
  // Add addresses to grid points
  const pointsWithAddresses = await Promise.all(
    gridPoints.map(async (point, index) => {
      if (onProgress) {
        onProgress(index + 1, gridPoints.length);
      }
      
      try {
        const address = await reverseGeocode(point.lat, point.lng, apiKey);
        return {
          ...point,
          id: point.label,
          address: address
        };
      } catch (error) {
        return {
          ...point,
          id: point.label,
          address: `${point.lat.toFixed(4)}, ${point.lng.toFixed(4)}`
        };
      }
    })
  );
  
  return pointsWithAddresses;
};


